import { useMfaEnrollment, type User, useUser } from '@privy-io/react-auth';
import { useCallback } from 'react';

import { useWalletAccess } from '@/features/wallet-access/wallet-access-provider';
import { type WalletOperation } from '@/features/wallet-access/wallet-operation';

import { WalletMfaMethod, type WalletTotpEnrollmentMaterial } from './wallet-mfa-types';
import { hasMfaMethod, requireConfirmedState } from './wallet-mfa-user-state';
import { useWalletSecurityOperation } from './wallet-mfa-verification-provider';

const hasTotp = (user: User) => hasMfaMethod(user, WalletMfaMethod.Totp);
const lacksTotp = (user: User) => !hasTotp(user);

export const useWalletMfaTotpManagement = () => {
  const { initEnrollmentWithTotp, submitEnrollmentWithTotp, unenrollWithTotp } = useMfaEnrollment();
  const { beginTotpOperation, runProtected, confirmProviderState } = useTotpOperations();

  const startTotpEnrollment = useCallback(async (): Promise<WalletTotpEnrollmentMaterial> => {
    const operation = beginTotpOperation();
    const { secret, authUrl } = await runProtected(operation, initEnrollmentWithTotp);
    return { sharedSecret: secret, setupUri: authUrl };
  }, [beginTotpOperation, initEnrollmentWithTotp, runProtected]);

  const submitTotpEnrollment = useCallback(
    async (code: string) => {
      const operation = beginTotpOperation();
      await runProtected(operation, () => submitEnrollmentWithTotp({ mfaCode: code }));
      await confirmProviderState(operation, hasTotp);
    },
    [beginTotpOperation, confirmProviderState, runProtected, submitEnrollmentWithTotp],
  );

  const removeTotp = useCallback(async () => {
    const operation = beginTotpOperation();
    await runProtected(operation, unenrollWithTotp);
    await confirmProviderState(operation, lacksTotp);
  }, [beginTotpOperation, confirmProviderState, runProtected, unenrollWithTotp]);

  return { startTotpEnrollment, submitTotpEnrollment, removeTotp };
};

function useTotpOperations() {
  const { refreshUser, user } = useUser();
  const { access, beginOperation } = useWalletAccess();
  const runSecurityOperation = useWalletSecurityOperation();
  const boundPrivyUserId = access?.privyUserId;

  const beginTotpOperation = useCallback((): WalletOperation => {
    const operation = beginOperation();
    operation.assertCurrent();
    requireConfirmedState(Boolean(user && user.id === boundPrivyUserId));
    return operation;
  }, [beginOperation, boundPrivyUserId, user]);

  const runProtected = useCallback(
    <T>(operation: WalletOperation, work: () => Promise<T>): Promise<T> =>
      operation.run(() => runSecurityOperation(work)),
    [runSecurityOperation],
  );

  const confirmProviderState = useCallback(
    async (operation: WalletOperation, isConfirmed: (refreshed: User) => boolean) => {
      const refreshed = await operation.run(refreshUser);
      requireConfirmedState(refreshed.id === boundPrivyUserId && isConfirmed(refreshed));
    },
    [boundPrivyUserId, refreshUser],
  );

  return { beginTotpOperation, runProtected, confirmProviderState };
}
